// ============================================================================
// TRIMA ENCRYPTION — Lightweight obfuscation for localStorage
// ============================================================================
// This is NOT cryptographic security — it's obfuscation. The goal is to prevent
// a curious user from reading their in-progress Trima answers via the browser's
// dev tools. The encryption key lives in the bundle, so anyone with code access
// can trivially decrypt.
//
// We use XOR + base64 to avoid pulling in a heavy dependency (crypto-js ~20kb)
// for a non-security use case. Zero npm deps = zero cleanup when removing Trima.
//
// If the threat model ever upgrades, replace this file's two functions with a
// proper crypto-js (AES) or Web Crypto API implementation. No other file needs
// to change.
// ============================================================================

import { TRIMA_ENCRYPTION_KEY } from 'pages/questionnaire-trima/constants/trima.constants';

const xorWithKey = (input: string, key: string): string => {
  let output = '';
  for (let i = 0; i < input.length; i++) {
    output += String.fromCharCode(input.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return output;
};

/**
 * Obfuscate a string for localStorage write.
 * Returns base64 (safe for localStorage), input passed through XOR with the key.
 */
export const trimaEncrypt = (plaintext: string): string => {
  try {
    const xored = xorWithKey(plaintext, TRIMA_ENCRYPTION_KEY);
    // btoa needs Latin1 chars only; we encode to handle any UTF-8 in plaintext
    return btoa(unescape(encodeURIComponent(xored)));
  } catch (e) {
    console.error('[trimaEncrypt] failed:', e);
    return '';
  }
};

/**
 * Reverse of trimaEncrypt. Returns null if input is malformed (e.g. legacy
 * unencrypted value, key changed, or tampering).
 */
export const trimaDecrypt = (ciphertext: string): string | null => {
  try {
    const xored = decodeURIComponent(escape(atob(ciphertext)));
    return xorWithKey(xored, TRIMA_ENCRYPTION_KEY);
  } catch (e) {
    console.error('[trimaDecrypt] failed:', e);
    return null;
  }
};
